/**
 * What an in-place organization switch must drop that React will not.
 *
 * A switch without a page reload (components/org-switch-boundary.tsx) remounts
 * the dashboard tree, which restarts every component's state, effect and
 * subscription. Module-level state survives a remount: caches, zustand stores,
 * toast queues, shared channels. Each module that holds organization data
 * outside React registers how to drop it here, and the boundary runs every
 * reset after the old tree has unmounted and before the new one mounts. A full
 * reload never needed this: it drops all JavaScript memory.
 *
 * Deliberately free of React and of any import, so any client module can
 * register without an import cycle.
 */

type Reset = () => void | Promise<void>

const resets = new Map<string, Reset>()

/** Registers (or replaces) the reset for `name`. Called at module scope. */
export function registerOrgSwitchReset(name: string, reset: Reset): void {
  resets.set(name, reset)
}

/**
 * Runs every registered reset. One that throws does not stop the others; the
 * names that failed are returned so the caller can fall back to a full reload.
 */
export async function runOrgSwitchResets(): Promise<string[]> {
  const failed: string[] = []
  for (const [name, reset] of resets) {
    try {
      await reset()
    } catch {
      failed.push(name)
    }
  }
  return failed
}

/** The registered names, for tests and diagnostics. */
export function registeredOrgSwitchResets(): string[] {
  return [...resets.keys()]
}
